import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Mail, Send, FileText, AlertCircle, Check, Loader2,
  Search, MessageSquare, Building2, CreditCard, Users, 
  Calendar, PartyPopper, KeyRound
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMAIL_TEMPLATES = [
  {
    id: 'chase_general',
    name: 'General Chase',
    icon: MessageSquare,
    description: 'Request general update from solicitor',
    recipients: ['seller_solicitor', 'buyer_solicitor'],
    category: 'chase'
  },
  {
    id: 'chase_searches',
    name: 'Chase Searches',
    icon: Search,
    description: 'Request search results status',
    recipients: ['buyer_solicitor'],
    category: 'chase'
  },
  {
    id: 'chase_enquiries',
    name: 'Chase Enquiries',
    icon: FileText,
    description: 'Request enquiry status',
    recipients: ['seller_solicitor', 'buyer_solicitor'],
    category: 'chase'
  },
  {
    id: 'chase_mortgage',
    name: 'Chase Mortgage',
    icon: CreditCard,
    description: 'Request mortgage update',
    recipients: ['buyer'],
    category: 'chase'
  },
  {
    id: 'memorandum_of_sale',
    name: 'Memorandum of Sale',
    icon: FileText,
    description: 'Send memo to seller solicitor with sale details',
    recipients: ['seller_solicitor'],
    category: 'memo'
  },
  {
    id: 'buyers_acceptance',
    name: 'Buyers Acceptance Letter',
    icon: Users,
    description: 'Send acceptance letter with memo to buyer',
    recipients: ['buyer'],
    category: 'memo'
  },
  {
    id: 'buyers_sol_exchange',
    name: 'Buyer Solicitor Exchange',
    icon: Building2,
    description: 'Notify buyer solicitor of exchange',
    recipients: ['buyer_solicitor'],
    category: 'exchange',
    requiresDate: true
  },
  {
    id: 'sellers_sol_exchange',
    name: 'Seller Solicitor Exchange',
    icon: Building2,
    description: 'Send invoice & exchange notice to seller solicitor',
    recipients: ['seller_solicitor'],
    category: 'exchange',
    requiresDate: true
  },
  {
    id: 'exchange_confirmation',
    name: 'Exchange Confirmation',
    icon: PartyPopper,
    description: 'Confirm exchange & completion date to vendor',
    recipients: ['seller'],
    category: 'milestone',
    requiresDate: true
  }
];

const RECIPIENT_LABELS = {
  seller_solicitor: "Seller's Solicitor",
  buyer_solicitor: "Buyer's Solicitor",
  buyer: "Buyer",
  seller: "Vendor",
  vendor: "Vendor",
  vendor_solicitor: "Vendor's Solicitor"
};

export function SalesEmailPanel({ caseData, onEmailSent }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [sending, setSending] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const token = localStorage.getItem('token');

  const fetchEmailLog = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/sales-emails/log/${caseData.case_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailLog(response.data.logs || []);
      setShowLog(true);
    } catch (error) {
      toast.error('Failed to load email log');
    }
  };

  const sendChaseEmail = async (chaseType, recipient) => {
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/sales-emails/chase`,
        {
          case_id: caseData.case_id,
          chase_type: chaseType,
          recipient_type: recipient,
          custom_message: customMessage || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Chase email sent to ${RECIPIENT_LABELS[recipient]}`);
      setSelectedTemplate(null);
      setSelectedRecipients([]);
      setCustomMessage('');
      if (onEmailSent) onEmailSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const sendMemorandum = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/sales-emails/memorandum`,
        {
          case_id: caseData.case_id,
          recipients: selectedRecipients
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.sent_to?.length > 0) {
        toast.success(`Memorandum sent to ${response.data.sent_to.length} recipients`);
      }
      if (response.data.failed?.length > 0) {
        toast.error(`Failed to send to: ${response.data.failed.join(', ')}`);
      }
      
      setSelectedTemplate(null);
      setSelectedRecipients([]);
      if (onEmailSent) onEmailSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send memorandum');
    } finally {
      setSending(false);
    }
  };

  const sendExchangeConfirmation = async () => {
    if (!completionDate) {
      toast.error('Please enter the completion date');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/sales-emails/exchange-confirmation`,
        {
          case_id: caseData.case_id,
          completion_date: completionDate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Exchange confirmation sent to vendor');
      setSelectedTemplate(null);
      setCompletionDate('');
      if (onEmailSent) onEmailSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleSend = () => {
    if (!selectedTemplate) return;
    
    if (selectedTemplate.id === 'memorandum_of_sale') {
      sendMemorandum();
    } else if (selectedTemplate.id === 'exchange_confirmation') {
      sendExchangeConfirmation();
    } else if (selectedTemplate.id === 'buyers_acceptance') {
      sendBuyersAcceptance();
    } else if (selectedTemplate.id === 'buyers_sol_exchange') {
      sendBuyersSolExchange();
    } else if (selectedTemplate.id === 'sellers_sol_exchange') {
      sendSellersSolExchange();
    } else {
      // For chase emails, send to first selected recipient
      const recipient = selectedRecipients[0];
      if (!recipient) {
        toast.error('Please select a recipient');
        return;
      }
      const chaseType = selectedTemplate.id.replace('chase_', '');
      sendChaseEmail(chaseType, recipient);
    }
  };

  const sendBuyersAcceptance = async () => {
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/sales-emails/buyers-acceptance`,
        { case_id: caseData.case_id, recipients: [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Buyers acceptance letter sent');
      setSelectedTemplate(null);
      if (onEmailSent) onEmailSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const sendBuyersSolExchange = async () => {
    if (!completionDate) {
      toast.error('Please enter the completion date');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/sales-emails/buyers-sol-exchange`,
        { case_id: caseData.case_id, completion_date: completionDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Exchange letter sent to buyer solicitor');
      setSelectedTemplate(null);
      setCompletionDate('');
      if (onEmailSent) onEmailSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const sendSellersSolExchange = async () => {
    if (!completionDate) {
      toast.error('Please enter the completion date');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/sales-emails/sellers-sol-exchange`,
        { case_id: caseData.case_id, completion_date: completionDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Exchange & invoice letter sent to seller solicitor');
      setSelectedTemplate(null);
      setCompletionDate('');
      if (onEmailSent) onEmailSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const toggleRecipient = (recipient) => {
    if (selectedRecipients.includes(recipient)) {
      setSelectedRecipients(selectedRecipients.filter(r => r !== recipient));
    } else {
      if (selectedTemplate?.id === 'memorandum_of_sale') {
        setSelectedRecipients([...selectedRecipients, recipient]);
      } else {
        setSelectedRecipients([recipient]);
      }
    }
  };

  const getRecipientEmail = (recipientType) => {
    switch (recipientType) {
      case 'seller_solicitor':
      case 'vendor_solicitor':
        return caseData.seller_solicitor_email;
      case 'buyer_solicitor':
        return caseData.buyer_solicitor_email;
      case 'buyer':
        return caseData.buyer_email;
      case 'seller':
      case 'vendor':
        return caseData.seller_email;
      default:
        return null;
    }
  };

  return (
    <Card data-testid="sales-email-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="w-5 h-5" />
            Send Emails
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchEmailLog}
            data-testid="view-email-log-btn"
          >
            View Log
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Template Selection */}
        {!selectedTemplate ? (
          <div className="grid grid-cols-1 gap-2">
            {EMAIL_TEMPLATES.map((template) => {
              const Icon = template.icon;
              return (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-amber-500 hover:bg-amber-50 transition-colors text-left"
                  data-testid={`email-template-${template.id}`}
                >
                  <div className="p-2 rounded-lg bg-slate-100">
                    <Icon className="w-4 h-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{template.name}</p>
                    <p className="text-xs text-slate-500">{template.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Selected Template Header */}
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2">
                <selectedTemplate.icon className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-sm">{selectedTemplate.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedTemplate(null);
                  setSelectedRecipients([]);
                  setCustomMessage('');
                  setCompletionDate('');
                }}
              >
                Change
              </Button>
            </div>

            {/* Recipient Selection - not shown for exchange confirmation */}
            {selectedTemplate.id !== 'exchange_confirmation' && (
            <div>
              <p className="text-sm font-medium mb-2">
                {selectedTemplate.id === 'memorandum_of_sale' 
                  ? 'Select Recipients:' 
                  : 'Select Recipient:'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {selectedTemplate.recipients.map((recipient) => {
                  const email = getRecipientEmail(recipient);
                  const isSelected = selectedRecipients.includes(recipient);
                  const hasEmail = !!email;
                  
                  return (
                    <button
                      key={recipient}
                      onClick={() => hasEmail && toggleRecipient(recipient)}
                      disabled={!hasEmail}
                      className={`p-2 rounded-lg border text-left transition-colors ${
                        isSelected 
                          ? 'border-amber-500 bg-amber-50' 
                          : hasEmail 
                            ? 'border-slate-200 hover:border-slate-300' 
                            : 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'
                      }`}
                      data-testid={`recipient-${recipient}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium">
                          {RECIPIENT_LABELS[recipient]}
                        </span>
                        {isSelected && <Check className="w-3 h-3 text-amber-600" />}
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-1">
                        {email || 'No email'}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {/* Exchange confirmation info */}
            {selectedTemplate.id === 'exchange_confirmation' && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <strong>🎉 Congratulations!</strong> This will send an exchange confirmation email to the vendor and update the case status to <strong>Exchanged</strong>.
                </p>
              </div>
            )}

            {/* Custom Message (for chase emails) */}
            {selectedTemplate.id.startsWith('chase_') && selectedTemplate.id !== 'chase_mortgage' && (
              <div>
                <label className="text-sm font-medium">Custom Message (optional):</label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Add any specific details..."
                  className="w-full mt-1 p-2 border rounded-lg text-sm resize-none"
                  rows={2}
                  data-testid="custom-message-input"
                />
              </div>
            )}

            {/* Completion Date (for exchange templates) */}
            {(selectedTemplate.id === 'exchange_confirmation' || 
              selectedTemplate.id === 'buyers_sol_exchange' || 
              selectedTemplate.id === 'sellers_sol_exchange') && (
              <div>
                <label className="text-sm font-medium">Completion Date:</label>
                <input
                  type="date"
                  value={completionDate}
                  onChange={(e) => setCompletionDate(e.target.value)}
                  className="w-full mt-1 p-2 border rounded-lg text-sm"
                  data-testid="completion-date-input"
                />
              </div>
            )}

            {/* Send Button */}
            <Button
              onClick={handleSend}
              disabled={sending || 
                (selectedTemplate.category === 'chase' && selectedRecipients.length === 0) || 
                (selectedTemplate.requiresDate && !completionDate)}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="send-email-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </div>
        )}

        {/* Email Log Modal */}
        {showLog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-lg m-4 max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold">Email History</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowLog(false)}>
                  Close
                </Button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {emailLog.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No emails sent yet</p>
                ) : (
                  <div className="space-y-3">
                    {emailLog.map((log, i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="w-3 h-3 text-slate-400" />
                          <span className="text-sm font-medium">{log.subject}</span>
                        </div>
                        <p className="text-xs text-slate-500">To: {log.recipient}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(log.sent_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SalesEmailPanel;
